import React, { useState, useEffect, useContext, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from '../Components/CheckoutForm';
import { STRIPE_PUBLIC_KEY } from '../Constants';
import { appUrl } from '../api/endpoints';
import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import planTranslations from '../languages/plan.json';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

export const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const planPageT = planTranslations[lang];
  const t = planPageT.payment;
  const isRtl = ['ar', 'ur'].includes(lang);

  const { amount, currency, plan_id, plan_name } = location.state || {};
  const normalizedCurrency =
    typeof currency === 'string' && currency.trim()
      ? currency.toLowerCase()
      : 'aed';
  const localeByLanguage = {
    en: 'en-US',
    ar: 'ar-AE',
    ur: 'ur-PK',
  };

  const formatPrice = (priceInMinorUnits, currencyCode = 'AED') => {
    const normalizedPrice = Number(priceInMinorUnits);
    const majorAmount = Number.isFinite(normalizedPrice) ? normalizedPrice / 100 : 0;
    const hasFraction = Number.isFinite(normalizedPrice) && normalizedPrice % 100 !== 0;

    return new Intl.NumberFormat(localeByLanguage[lang] || 'en-US', {
      style: 'currency',
      currency: String(currencyCode || 'AED').toUpperCase(),
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(majorAmount);
  };

  const paymentLabel =
    typeof plan_name === 'string' && plan_name.trim()
      ? `${plan_name} - ${formatPrice(amount, normalizedCurrency)} ${planPageT.unitPerMonth || '/ month'}`
      : formatPrice(amount, normalizedCurrency);

  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    type: 'info',
    position: isRtl ? 'left' : 'right',
  });

  const user = JSON.parse(localStorage.getItem('iot-user') || '{}');
  const subscriberId = user?.subid;

  useEffect(() => {
    if (
      typeof amount !== 'number' ||
      typeof plan_id !== 'number'
    ) {
      navigate('/devices', { replace: true });
    }
  }, [amount, plan_id, navigate]);

  useEffect(() => {
    if (!subscriberId) {
      setAlertState({
        open: true,
        type: 'error',
        position: isRtl ? 'left' : 'right',
        message: t.alertNoSubscriber,
      });
      setTimeout(() => navigate('/devices', { replace: true }), 3000);
    }
  }, [subscriberId, navigate, isRtl, t.alertNoSubscriber]);


  const showAlert = (type, message) => {
    setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        appUrl('payment/create-payment-intent'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            currency: normalizedCurrency,
            subscriber_id: Number(subscriberId),
            plan_id,
          }),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.error || 'Failed to create payment intent.');
      }

      const { clientSecret: cs } = await resp.json();
      setClientSecret(cs);
    } catch (err) {
      console.error('Error creating PaymentIntent:', err);
      showAlert('error', err.message || 'Could not initiate payment session.');
    } finally {
      setLoading(false);
    }
  };

  if (clientSecret) {
    return (
      <Elements
        stripe={stripePromise}
        options={{ clientSecret, appearance: { theme: 'stripe' } }}
      >
        <CheckoutForm />
      </Elements>
    );
  }

  return (
    <Fragment>
      <Alert
        open={alertState.open}
        close={() => setAlertState({ ...alertState, open: false })}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />
      {/* Only render the payment confirmation if subscriberId exists */}
      {subscriberId && (
        <div style={{ padding: '2rem', maxWidth: 500, margin: '0 auto' }}>
          <h2>{t.header}</h2>
          <p>{t.selection}{' '}<strong>{paymentLabel}</strong></p>
          <button
            onClick={handleSubscribe}
            disabled={loading}
            style={{
              marginTop: '1rem',
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              backgroundColor: '#6772e5',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading
              ? t.loading
              : t.button.replace('{{label}}', paymentLabel)}
          </button>
        </div>
      )}
    </Fragment>
  );
};
